// services/api.js - Configuración base de la API
import axios from 'axios';
import { getToken, setToken, removeToken, getRefreshToken, setRefreshToken } from '../utils/auth';

// Configuración de URL de API para múltiples entornos
const getApiBaseUrl = () => {
  // En producción, usar la variable de entorno
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // En desarrollo local
  if (process.env.NODE_ENV === 'development') {
    // Si estamos accediendo desde un subdominio local (*.herrerahavas.local)
    // necesitamos usar el MISMO hostname completo con el puerto del backend
    const hostname = window.location.hostname;
    
    if (hostname.includes('.herrerahavas.local') || hostname.includes('.herrerahavas.com')) {
      // Mantener el hostname COMPLETO (incluyendo subdominio) con el puerto del backend
      const protocol = window.location.protocol;
      return `${protocol}//${hostname}:3001/api`;
    }
    
    // Si estamos en localhost normal
    return 'http://localhost:3001/api';
  }
  
  // En producción sin variable de entorno, el API va al hostname nativo de Azure
  // Container Apps (cert valido de *.azurecontainerapps.io) — asi no hace falta DNS
  // ni certificado propio, y CORS ya permite el origen. La app vive en
  // *.herrerahavas.com.ec; el viejo galaxy-api.herrerahavas.com quedo muerto tras el
  // secuestro del dominio .com, por eso ya NO se usa como fallback.
  if (process.env.NODE_ENV === 'production') {
    return 'https://galaxy-backend.grayrock-48a77db7.southcentralus.azurecontainerapps.io/api';
  }
  
  // Fallback
  return 'http://localhost:3001/api';
};

const API_BASE_URL = getApiBaseUrl();

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 1800000, // 30 minutos para consultas complejas de Infomedia e IA y carga masiva
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Función para detectar tenant por subdomain
const getTenantFromSubdomain = () => {
  const hostname = window.location.hostname;
  
  // Mapeo de subdominios a códigos de tenant
  const subdomainToTenantMap = {
    'galaxy': 'HERRERA_HAVAS',
    'ford': 'FORD',
    'claro': 'CLARO',
    'utpl': 'UTPL',
    'danec': 'DANEC',
    'trivo': 'TRIVO',
    'tygo': 'TYGO',
    'admin': 'QURAFLOW'
  };
  
  // Extraer subdomain
  const parts = hostname.split('.');
  
  // ✅ CASO ESPECIAL: subdominio.localhost (ej: claro.localhost)
  // En este caso parts = ['claro', 'localhost'], length = 2
  if (parts.length === 2 && parts[1] === 'localhost') {
    const subdomain = parts[0];
    console.log(`[API] 🔍 Detectado subdomain en localhost: ${subdomain}`);
    return subdomainToTenantMap[subdomain] || 'HERRERA_HAVAS';
  }
  
  // Caso normal: subdominio.dominio.tld (ej: claro.herrerahavas.com)
  if (parts.length >= 3) {
    const subdomain = parts[0];
    return subdomainToTenantMap[subdomain] || 'HERRERA_HAVAS';
  }
  
  // Para localhost simple o sin subdomain
  return 'HERRERA_HAVAS';
};

// Interceptor para agregar token de autenticación
apiClient.interceptors.request.use((config) => {
  const token = getToken();
  // No sobreescribir un Authorization explícito (p.ej. /auth/refresh usa el refresh token).
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Agregar X-Tenant-ID correcto
  if (!config.headers['X-Tenant-ID']) {
    try {
      // PRIORIDAD 1: Usar configuración de empresa del localStorage (después del login)
      const companyConfig = localStorage.getItem('companyConfig');
      if (companyConfig) {
        const config_obj = JSON.parse(companyConfig);
        config.headers['X-Tenant-ID'] = config_obj.code;
      } else {
        // PRIORIDAD 2: Detectar por subdomain (para el login inicial)
        config.headers['X-Tenant-ID'] = getTenantFromSubdomain();
      }
    } catch (error) {
      console.warn('Error determining tenant:', error);
      // FALLBACK: Detectar por subdomain
      config.headers['X-Tenant-ID'] = getTenantFromSubdomain();
    }
  }
  
  return config;
});

// Variable para evitar múltiples refreshes simultáneos
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Interceptor para manejar respuestas de error y auto-refresh
apiClient.interceptors.response.use(
  (response) => {
    // El backend siempre devuelve { success, data, error }
    // Para mantener compatibilidad, podemos procesar esto aquí
    if (response.data && response.data.success === false) {
      // Tratar respuestas con success=false como errores
      const error = new Error(response.data.error?.message || response.data.error || 'Error del servidor');
      error.response = {
        status: 400,
        data: response.data
      };
      throw error;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Si es un error 401 y no hemos intentado refresh aún
    const authEndpoints = ['/auth/login', '/auth/register', '/auth/refresh'];
    const isAuthEndpoint = authEndpoints.some(endpoint => originalRequest.url?.includes(endpoint));
    
    const hasRefreshToken = localStorage.getItem('galaxy_refresh_token');
    
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint && hasRefreshToken) {
      
      if (isRefreshing) {
        // Ya hay un refresh en progreso, agregar a la cola
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Intentar refresh token
        // Tomar refresh token desde utilidades (fuente única)
        const refreshToken = getRefreshToken();
        
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }
        
        // Usar un cliente axios sin interceptores para evitar loops
        const bare = axios.create({ baseURL: API_BASE_URL, withCredentials: true });
        const response = await bare.post('/auth/refresh', {}, {
          headers: { 'Authorization': `Bearer ${refreshToken}` }
        });

        // El backend envuelve la respuesta en { success, data }: los tokens vienen en data.
        // Se contempla también el caso sin envolver por compatibilidad.
        const body = response.data?.data || response.data;
        const { accessToken, refreshToken: newRefreshToken } = body;

        if (!accessToken) {
          throw new Error('Respuesta de refresh sin accessToken');
        }

        // Actualizar tokens
        setToken(accessToken);
        if (newRefreshToken) setRefreshToken(newRefreshToken);
        
        // Procesar cola de peticiones fallidas
        processQueue(null, accessToken);
        
        // Reintentar la petición original con el nuevo token
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return apiClient(originalRequest);

      } catch (refreshError) {
        // Refresh token falló - hacer logout
        processQueue(refreshError, null);
        
        // Limpiar tokens locales
        removeToken();
        localStorage.removeItem('galaxy_refresh_token');
        
        // Disparar evento de logout
        window.dispatchEvent(new CustomEvent('auth:token-expired'));
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Servicios de usuarios
export const userService = {
  // Obtener todos los usuarios (solo admin)
  getAll: async () => {
    const response = await apiClient.get('/auth/users');
    // El backend devuelve {success: boolean, data: array}
    return response.data.data || response.data;
  },

  // Obtener usuario por ID
  getById: async (id) => {
    const response = await apiClient.get(`/auth/users/${id}`);
    return response.data.data || response.data;
  },

  // Crear nuevo usuario
  create: async (userData) => {
    const response = await apiClient.post('/auth/register', userData);
    return response.data.data || response.data;
  },

  // Actualizar usuario
  update: async (id, userData) => {
    const response = await apiClient.patch(`/auth/users/${id}`, userData);
    return response.data.data || response.data;
  },

  // Eliminar usuario
  delete: async (id) => {
    const response = await apiClient.delete(`/auth/users/${id}`);
    return response.data.data || response.data;
  },

  // Cambiar estado del usuario
  toggleStatus: async (id, isActive) => {
    const response = await apiClient.patch(`/auth/users/${id}`, { isActive });
    return response.data.data || response.data;
  },


  // Obtener estadísticas de sesiones de usuarios
  getSessionStats: async () => {
    const response = await apiClient.get('/auth/users/session-stats');
    return response.data.data || response.data;
  }
};

// Servicios de roles
export const roleService = {
  // Obtener todos los roles
  getAll: async () => {
    const response = await apiClient.get('/auth/roles');
    // El backend devuelve {success: boolean, data: array}
    return response.data.data || response.data;
  },

  // Obtener rol por ID
  getById: async (id) => {
    const response = await apiClient.get(`/auth/roles/${id}`);
    return response.data.data || response.data;
  },

  // Crear nuevo rol
  create: async (roleData) => {
    const response = await apiClient.post('/auth/roles', roleData);
    return response.data.data || response.data;
  },

  // Actualizar rol
  update: async (id, roleData) => {
    const response = await apiClient.patch(`/auth/roles/${id}`, roleData);
    return response.data.data || response.data;
  },

  // Eliminar rol
  delete: async (id) => {
    const response = await apiClient.delete(`/auth/roles/${id}`);
    return response.data.data || response.data;
  }
};

// Servicio de autenticación
export const authService = {
  // Login
  login: async (credentials) => {
    const response = await apiClient.post('/auth/login', credentials);
    if (response.data.access_token) {
      setToken(response.data.access_token);
    }
    return response.data;
  },

  // Logout
  logout: async () => {
    await apiClient.post('/auth/logout');
    removeToken();
  },

  // Obtener perfil del usuario actual
  getProfile: async () => {
    const response = await apiClient.get('/auth/profile');
    return response.data;
  },

  // Actualizar perfil del usuario actual
  updateProfile: async (profileData) => {
    const response = await apiClient.patch('/auth/profile', profileData);
    return response.data.data || response.data;
  },

  // Refrescar token
  refreshToken: async () => {
    const response = await apiClient.post('/auth/refresh');
    if (response.data.accessToken) {
      setToken(response.data.accessToken);
    }
    return response.data;
  }
};

// Servicios de áreas de negocio
export const businessAreaService = {
  // Obtener todas las áreas de negocio
  getAll: async () => {
    const response = await apiClient.get('/business-areas');
    // El backend devuelve {success: boolean, data: array}
    return response.data.data || response.data;
  },

  // Obtener área por ID
  getById: async (id) => {
    const response = await apiClient.get(`/business-areas/${id}`);
    return response.data.data || response.data;
  },

  // Crear nueva área de negocio
  create: async (areaData) => {
    const response = await apiClient.post('/business-areas', areaData);
    return response.data.data || response.data;
  },

  // Actualizar área de negocio
  update: async (id, areaData) => {
    const response = await apiClient.patch(`/business-areas/${id}`, areaData);
    return response.data.data || response.data;
  },

  // Eliminar área de negocio
  delete: async (id) => {
    const response = await apiClient.delete(`/business-areas/${id}`);
    return response.data.data || response.data;
  }
};

export default apiClient; 